





























































































































import Vue, { PropType } from 'vue';
import { OrgUser } from '@/models/orgStructure';
import { MemberRole, OrgRole, WebRequestStatus } from '@/models';
import { OrgRoleFilter, orgRoleOf } from '@/orgRole';
import store from '@/store';
import * as Utils from '@/utils';

import LazyRenderingList from '@/components/LazyRenderingList.vue';
import OrganizationRoleFilterControl from '@/components/OrganizationRoleFilterControl.vue';
import OrganizationRoleInfo from '@/components/info/OrganizationRoleInfo.vue';
import OrganizationUserSlug from '@/components/OrganizationUserSlug.vue';
import OrganizationUserCreate from '@/components/OrganizationUserCreate.vue';
import OrganizationUserCreateMultiple from '@/components/OrganizationUserCreateMultiple.vue';
import OrganizationUserMembership from '@/components/OrganizationUserMembership.vue';
import OrgRoles from '@/components/roles/OrgRoles.vue';
import PersistableItem from '@/components/PersistableItem.vue';
import RoleSwitch from '@/components/RoleSwitch.vue';

type MembershipMap = {
  [key in MemberRole]: number;
};

interface OrgUserWithRolesMap extends OrgUser {
  roles: MembershipMap;
  isActive: boolean;
}

type UserStatusList = [OrgUserWithRolesMap, WebRequestStatus][];

const ROLE_NAMES: { [role in OrgRole]: string } = {
  [OrgRole.ADMIN]: 'admin',
  [OrgRole.ORGANIZER]: 'organizer',
  [OrgRole.MEMBER]: 'member'
};

const ROLE_PLURALS: { [role in OrgRole]: string } = {
  [OrgRole.ADMIN]: 'admins',
  [OrgRole.ORGANIZER]: 'organizers',
  [OrgRole.MEMBER]: 'members'
};

export default Vue.extend({
  components: {
    RoleSwitch,
    LazyRenderingList,
    OrganizationRoleFilterControl,
    OrganizationRoleInfo,
    OrganizationUserSlug,
    OrganizationUserCreate,
    OrganizationUserCreateMultiple,
    OrganizationUserMembership,
    OrgRoles,
    PersistableItem
  },
  props: {
    filterInput: {
      type: String,
      required: true
    },
    isFilterFocused: Boolean,
    users: Array as PropType<[OrgUser, WebRequestStatus][]>,
    createEnabled: Boolean,
    // Bound with .sync.  Deliberately not saved: a filter that comes back on
    // its own next visit would look like members had gone missing.
    roleFilter: {
      type: String as PropType<OrgRoleFilter>,
      default: 'ALL' as OrgRoleFilter
    }
  },
  computed: {
    filter(): string {
      return Utils.trimBrackets(this.filterInput.trim());
    },

    all(): OrgUserWithRolesMap[] {
      return this.withRoles.map(([u]) => u);
    },

    withRoles(): UserStatusList {
      const withRolesAndIsActive: [
        OrgUserWithRolesMap,
        WebRequestStatus
      ][] = this.users.map(([u, status]) => {
        const rolesMap = u.memberships.reduce(
          (ships: MembershipMap, membership) => {
            ships[membership.role] = (ships[membership.role] ?? 0) + 1;
            return ships;
          },
          {} as MembershipMap
        );

        const isActive = u.memberships.some(m => m.isActive);

        return [
          {
            ...u,
            roles: rolesMap,
            isActive
          },
          status
        ];
      });

      const [active, archived] = withRolesAndIsActive.reduce(
        (acc, elem) => {
          const [user] = elem;
          const [active, archived] = acc;
          if (user.isActive) {
            return [[...active, elem], archived];
          } else {
            return [active, [...archived, elem]];
          }
        },
        [[], []] as [UserStatusList, UserStatusList]
      );

      return [...active, ...archived];
    },

    showToolbar(): boolean {
      return this.users.length > 0;
    },

    // Matches for the search text, in every role.
    searched(): UserStatusList {
      if (!this.filter) {
        return this.withRoles;
      }

      return Utils.findTerm(this.filter, this.withRoles, ([user]) => [
        user.name,
        user.emails.map(ue => ue.email).join(' ')
      ]);
    },

    // Orphans hold no org role, so any role filter hides them.
    filtered(): UserStatusList {
      if (this.roleFilter === 'ALL') {
        return this.searched;
      }

      return this.searched.filter(
        ([user]) => orgRoleOf(user) === this.roleFilter
      );
    },

    hiddenByRole(): number {
      return this.searched.length - this.filtered.length;
    },

    // Explains an empty list the role filter caused.  A search nobody in any
    // role matches gets the create form instead.
    showRoleHint(): boolean {
      return (
        this.roleFilter !== 'ALL' &&
        this.users.length > 0 &&
        this.filtered.length == 0 &&
        !this.showMultiUserCreate &&
        (!this.filter || this.hiddenByRole > 0)
      );
    },

    roleHintText(): string {
      if (this.roleFilter === 'ALL') {
        return '';
      }

      const plural = ROLE_PLURALS[this.roleFilter];

      return this.filter
        ? `No ${plural} match "${this.filter}".`
        : `No ${plural} in this organization.`;
    },

    hiddenByRoleText(): string {
      if (!this.filter || this.hiddenByRole == 0) {
        return '';
      }

      const noun = this.hiddenByRole == 1 ? 'match' : 'matches';

      return `${this.hiddenByRole} ${noun} in other roles.`;
    },

    showUserCreate(): boolean {
      return (
        this.createEnabled && this.noFilterMatch && !this.showMultiUserCreate
      );
    },

    // Ignores the role filter, so we never offer to add someone who's already
    // in the org under another role.
    noFilterMatch(): boolean {
      return this.users.length > 0 && this.searched.length == 0;
    },

    showMultiUserCreate(): boolean {
      if (!this.createEnabled) {
        return false;
      }

      const tokenCount = Utils.parseByDelimiter(this.filterInput.trim()).length;

      return tokenCount > 1;
    },

    // New users join in the filtered role, or as members under ALL; the
    // create forms name it so nobody adds an admin by accident.
    newMemberRole(): OrgRole {
      return this.roleFilter === 'ALL' ? OrgRole.MEMBER : this.roleFilter;
    },

    newMemberRoleName(): string {
      return ROLE_NAMES[this.newMemberRole];
    },

    newMemberPluralTitle(): string {
      const plural = ROLE_PLURALS[this.newMemberRole];

      return plural.charAt(0).toUpperCase() + plural.slice(1);
    }
  },

  methods: {
    // This narrows the type from OrganizationUserCreateMultiple, but we know we can
    // only get emails here.
    createUser(email: string) {
      this.$emit('create-user', email);
    },

    createUsers(emails: string[]) {
      this.$emit('create-users', emails);
    },

    selectRoleFilter(roleFilter: OrgRoleFilter) {
      this.$emit('update:roleFilter', roleFilter);
    },

    showAllRoles() {
      this.selectRoleFilter('ALL');
    },

    userClick({ userId }: OrgUserWithRolesMap) {
      const orgId = this.$route.params['organizationId'] as string;
      this.$router.push({
        name: 'OrganizationUser',
        params: { organizationId: orgId, userId: `${userId}` }
      });
    },

    updateFilter(value: string) {
      this.$emit('update-filter', value);
    },

    // OrganizationUserCreateMultiple emits (OrgUser | string)[], but
    // since we know we aren't passing in any allowedUsers (only blockingUsers),
    // we know the emitted unions must be all emails (string[]).
    addAllUsers(emails: string[]) {
      this.$emit('create-users', emails);
    },

    clearFilter() {
      this.$emit('update-filter', '');
    },

    changeOrgRole(orgUser: OrgUser) {
      store.dispatch.org.updateUserRoles(orgUser);
    }
  },

  watch: {
    filtered(current, prev) {
      if (current.length != prev.length) {
        this.$emit('filter', { count: current.length });
      }
    }
  }
});
